import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Avatar,
  Divider,
  Chip,
} from '@mui/material';
import {
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Business as BusinessIcon,
  Security as SecurityIcon,
  LocationOn as LocationIcon,
} from '@mui/icons-material';
import { useAppSelector } from '../hooks/redux';
import { usersAPI } from '../services/api';
import SkeletonLoader from '../components/common/SkeletonLoader';

interface UserDetails {
  id: string;
  firstname: string | null;
  lastname: string | null;
  cellnumber: string;
  address1: string | null;
  address2: string | null;
  profilepic: string | null;
  userstatus: string;
  registervia: string;
  roleId: number;
  companyname: string;
  email: string;
  countrycode: string;
  emailVerified: boolean;
  created: string;
  modified: string;
  masterdetailId: string;
  isregistered?: boolean;
  username?: string; // Added username to the interface
}

const Settings: React.FC = () => {
  const { user } = useAppSelector((state) => state.auth);
  const [userDetails, setUserDetails] = useState<UserDetails | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchUserDetails = async () => {
      const userId = user?.userId || localStorage.getItem('userId');

      if (userId) {
        try {
          setLoading(true);
          const response = await usersAPI.getUserById(userId);
          setUserDetails(response.data);
        } catch (error: any) {
          console.error('Error fetching user details:', error);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchUserDetails();
  }, [user?.userId]);

  if (loading) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom sx={{ mb: 4, fontSize: '1.25rem' }}>
          About Info
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3, mb: 3 }}>
          {/* User Profile Card Skeleton */}
          <Box sx={{ flex: 1 }}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <SkeletonLoader type="cards" count={1} />
              </CardContent>
            </Card>
          </Box>

          {/* System Information Card Skeleton */}
          <Box sx={{ flex: 1 }}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <SkeletonLoader type="cards" count={1} />
              </CardContent>
            </Card>
          </Box>
        </Box>

        {/* Application Information Card Skeleton */}
        <Card>
          <CardContent>
            <SkeletonLoader type="cards" count={1} />
          </CardContent>
        </Card>
      </Box>
    );
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getFullName = () => {
    // Try to get username first, then fallback to email username part
    if (userDetails?.username) {
      return userDetails.username;
    } else if (userDetails?.email) {
      // Extract username from email (part before @)
      const emailUsername = userDetails.email.split('@')[0];
      return emailUsername;
    }
    return user?.name || 'User Name';
  };

  return (
    <Box sx={{ display: 'flex', mx: 'auto', maxWidth: '767px', flexDirection: 'column', gap: 2, justifyContent: 'center' }}>
      {/* User Profile Card */}
      <Box sx={{ flex: 1 }}>
        <Card sx={{ height: '100%' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Avatar
                src={userDetails?.profilepic || undefined}
                sx={{ width: 56, height: 56, mr: 2, bgcolor: 'primary.main' }}
              >
                <PersonIcon sx={{ fontSize: 40 }} />
              </Avatar>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: .4 }}>
                  {getFullName()}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                  {user?.role || 'Admin'}
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ mb: 3 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <BusinessIcon sx={{ mr: 2, color: 'text.secondary' }} />
              <Box>
                <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, letterSpacing: .6, fontSize: '.65rem', textTransform: 'uppercase', color: 'text.secondary' }}>Company</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {userDetails?.companyname || 'N/A'}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', width: '50%' }}>
                <EmailIcon sx={{ mr: 2, color: 'text.secondary' }} />
                <Box>
                  <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, letterSpacing: .6, fontSize: '.65rem', textTransform: 'uppercase', color: 'text.secondary' }}>Email</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {userDetails?.email || 'N/A'}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', width: '50%' }}>
                <PhoneIcon sx={{ mr: 2, color: 'text.secondary' }} />
                <Box>
                  <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, letterSpacing: .6, fontSize: '.65rem', textTransform: 'uppercase', color: 'text.secondary' }}>Phone</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {userDetails?.countrycode} {userDetails?.cellnumber || 'N/A'}
                  </Typography>
                </Box>
              </Box>
            </Box>
            {userDetails?.address1 && (
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <LocationIcon sx={{ mr: 2, color: 'text.secondary' }} />
                <Box>
                  <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, letterSpacing: .6, fontSize: '.65rem', textTransform: 'uppercase', color: 'text.secondary' }}>Address</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {userDetails.address1}
                    {userDetails.address2 && `, ${userDetails.address2}`}
                  </Typography>
                </Box>
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>

      {/* System Information Card */}
      <Box sx={{ flex: 1 }}>
        <Card sx={{ height: '100%' }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 800, fontSize: '.95rem', color: 'primary.main', letterSpacing: .5, display: 'flex', alignItems: 'center' }} gutterBottom>
              <SecurityIcon sx={{ mr: 1 }} />
              System Information
            </Typography>

            <Divider sx={{ mb: 3 }} />

            <Box sx={{ mb: 3 }}>
              <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, letterSpacing: .6, fontSize: '.65rem', textTransform: 'uppercase', color: 'text.secondary', mb: .5 }}>User ID</Typography>
              <Chip label={userDetails?.id || 'N/A'} variant="outlined" size="small" sx={{ fontFamily: 'monospace', fontWeight: 600 }} />
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, letterSpacing: .6, fontSize: '.65rem', textTransform: 'uppercase', color: 'text.secondary', mb: .5 }}>Account Status</Typography>
              <Chip label={userDetails?.userstatus || 'N/A'} color={userDetails?.userstatus === 'Active' ? 'success' : 'error'} size="small" sx={{ fontWeight: 600 }} />
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, letterSpacing: .6, fontSize: '.65rem', textTransform: 'uppercase', color: 'text.secondary', mb: .5 }}>Email Verified</Typography>
              <Chip label={userDetails?.emailVerified ? 'Yes' : 'No'} color={userDetails?.emailVerified ? 'success' : 'warning'} size="small" sx={{ fontWeight: 600 }} />
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Application Information Card */}
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 800, fontSize: '.95rem', color: 'primary.main', letterSpacing: .5 }} gutterBottom>
            Account Information
          </Typography>

          <Divider sx={{ mb: 3 }} />

          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, letterSpacing: .6, fontSize: '.65rem', textTransform: 'uppercase', color: 'text.secondary', mb: .5 }}>Created Date</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {userDetails?.created ? formatDate(userDetails.created) : 'N/A'}
              </Typography>
            </Box>

            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, letterSpacing: .6, fontSize: '.65rem', textTransform: 'uppercase', color: 'text.secondary', mb: .5 }}>Last Modified</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {userDetails?.modified ? formatDate(userDetails.modified) : 'N/A'}
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Settings;